import { Fragment } from 'react'
import { useRouter } from 'next/router'
import { GetStaticPaths, GetStaticPropsContext, NextPage } from 'next'
import { useTranslation } from 'next-i18next'

import MediaMediabankDetail from '~/static-pages/media/pages/media-mediabank-detail/MediaMediabankDetail.page'
import MediaPageWrapper from '~/static-pages/media/components/MediaPageWrapper/MediaPageWrapper'
import Metatags from '~/components/Metadata/Metatags'
import { Locale, mediabankLocalePaths } from '~/../utils/sitemaps/mediabankAlternates'
import { DEFAULT_LAYOUT_TRANSLATION_MODULE_NAMES } from '~/shared/layouts/constants'
import { DefaultLayout } from '~/shared/layouts/DefaultLayout'
import { MediabankAsset } from '~/types/media'
import { NavigationElement } from '~/static-pages/media/components/MediaPageWrapper/MediaPageWrapperNav/MediaPageWrapperNav.types'
import { usePageTitle } from '~/utils/tags'

import {
  getMediabankAssetDetail,
  getRunningQueriesThunk,
  useGetMediabankAssetDetailQuery,
} from '~/redux/apis/media.api'
import { GetProps } from '~/utils/pageWrapper/types'
import { getStaticPropsWrapper } from '~/utils/pageWrapper/staticPageWrapper'

const MediaMediabankDetailPage: NextPage = () => {
  const { t } = useTranslation('media')
  const { locale, query, isFallback } = useRouter()
  const { data } = useGetMediabankAssetDetailQuery(
    { language: locale as string, slug: query.slug as string },
    { skip: isFallback },
  )

  const asset = data as MediabankAsset
  const title = usePageTitle(t('mediabankDetailTitle', { title: asset.title }))

  return (
    <Fragment>
      <Metatags localePaths={mediabankLocalePaths(locale as Locale, query.slug as string)} title={title} />

      <DefaultLayout>
        <MediaPageWrapper activeNavElement={NavigationElement.MEDIABANK}>
          <MediaMediabankDetail asset={asset} />
        </MediaPageWrapper>
      </DefaultLayout>
    </Fragment>
  )
}

export const getStaticPaths: GetStaticPaths = () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getProps: GetProps =
  (store) =>
  async ({ locale, params }: GetStaticPropsContext<{ slug: string }>) => {
    store.dispatch(
      getMediabankAssetDetail.initiate({
        language: locale as string,
        slug: params?.slug as string,
      }),
    )

    await Promise.all(store.dispatch(getRunningQueriesThunk()))

    const queries = store.getState().mediaApi.queries
    const query = Object.values(queries).find((q) => q?.endpointName === 'getMediabankAssetDetail')
    const data = query ? (query.data as Record<string, unknown>) : {}

    if (Object.keys(data).length === 0) {
      return {
        notFound: true,
      }
    }

    return {
      props: {
        namespaces: ['media', ...DEFAULT_LAYOUT_TRANSLATION_MODULE_NAMES],
      },
      revalidate: process.env.ENVIRONMENT === 'staging' ? 30 : false,
    }
  }

export const getStaticProps = getStaticPropsWrapper(getProps)
export default MediaMediabankDetailPage
